import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import RouteLayout from "../../../../Components/Layout/RouteLayout";
import {
  useGetBookingComponentQuery,
  useGetBookingFacilityQuery,
  useViewBookingDetailsQuery,
} from "../../../../../api/financeApi";
import { useDispatch, useSelector } from "react-redux";
import {
  GetAllEmployees,
  UpdateBookingService,
} from "../../../../../../utils/redux/HR/HRSlice";
import { toast } from "react-toastify";

const UpdateServiceBookings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams(); // Fetch the booking ID from the URL params

  const [facilityName, setFacilityName] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [optionalComponent, setOptionalComponent] = useState([]);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [additionalNote, setAdditionalNote] = useState("");

  // Fetching the required data
  const { data } = useViewBookingDetailsQuery(id); // Fetch booking details
  const { data: item } = useGetBookingFacilityQuery({
    pageSize: 10000,
    pageNumber: 1,
  });
  const { data: facilityData } = useGetBookingComponentQuery({
    pageSize: 10000,
    pageNumber: 1,
    FacilityId: facilityName,
  });
  const employee = useSelector((x) => x?.hr?.all_employees?.result);

  useEffect(() => {
    dispatch(GetAllEmployees({ pageSize: 100000, pageNumber: 1 }));
  }, [dispatch]);

  // Update form fields with the data from the API
  useEffect(() => {
    if (data?.result) {
      const booking = data.result;
      setFacilityName(booking.facilityId);
      setEmployeeName(booking.requisitorId);
      setOptionalComponent(booking.components.split(",").map(Number));
      setStartDate(booking.startDate.split("T")[0]); // Convert to date format
      setEndDate(booking.endDate.split("T")[0]); // Convert to date format
      setAdditionalNote(booking.specialRequest);
    }
  }, [data]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const employeeDetails = employee?.find((emp) => emp?.id === employeeName);
    const serviceId = item?.result?.items?.find(
      (fac) => fac?.id === facilityName
    );

    const payload = {
      id: id, // Uncomment this if the API requires an `id`
      requisitorId: employeeName,
      requisitorName: `${employeeDetails?.firstName} ${employeeDetails?.surname}`,
      serviceId: serviceId?.bookingServiceId,
      facilityId: facilityName,
      components: `${optionalComponent}`,
      startDate,
      endDate,
      specialRequest: additionalNote,
    };

    try {
      const response = await dispatch(UpdateBookingService(payload)).then(
        (res) => {
          if (res?.payload?.successful === true) {
            toast.success(res?.payload?.statusMessage);
            navigate(-1);
          } else {
            console.log("API response data:", res?.payload);
            toast.error(res?.payload?.validationErrors[0]?.replace(",", ""));
          }
        }
      );
    } catch (error) {
      console.error("An error occurred:", error);
      toast.error("An unexpected error occurred. Please try again later.");
    }
  };

  return (
    <RouteLayout title="Update Service Booking">
      <Box sx={{ padding: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Typography variant="h4" gutterBottom>
          Update Book a Service
        </Typography>
        <div className="bg-white shadow-md rounded-md p-10">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body1" fontWeight="bold">
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="facility-name-label">
                        Facility Name *
                      </InputLabel>
                      <Select
                        labelId="facility-name-label"
                        value={facilityName}
                        onChange={(e) => setFacilityName(e.target.value)}
                      >
                        {item?.result?.items?.map((fac) => (
                          <MenuItem key={fac?.id} value={fac?.id}>
                            {fac?.facilityName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="optional-component-label">
                        Optional Components
                      </InputLabel>
                      <Select
                        labelId="optional-component-label"
                        multiple
                        value={optionalComponent}
                        onChange={(e) => setOptionalComponent(e.target.value)}
                      >
                        {facilityData?.result?.items?.map((comp) => (
                          <MenuItem key={comp?.id} value={comp?.id}>
                            {comp?.componentName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="employee-name-label">
                        Employee Name
                      </InputLabel>
                      <Select
                        labelId="employee-name-label"
                        value={employeeName}
                        onChange={(e) => setEmployeeName(e.target.value)}
                      >
                        {employee?.map((emp) => (
                          <MenuItem key={emp?.id} value={emp?.id}>
                            {emp?.firstName} {emp?.surname}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="date"
                      fullWidth
                      label="Start Date *"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="date"
                      fullWidth
                      label="End Date *"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Note"
                      multiline
                      rows={4}
                      value={additionalNote}
                      onChange={(e) => setAdditionalNote(e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 4,
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{ marginRight: 2 }}
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" color="success" type="submit">
                    Submit
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </div>
      </Box>
    </RouteLayout>
  );
};

export default UpdateServiceBookings;
